import { Button, Input } from "@appkit4/react-components";
import { useQueryClient } from "@tanstack/react-query";
import { FormError, ValidationError, toastMessage } from "components/common/helpers";
import { FC, useState } from "react";
import { useParams } from "react-router-dom";
import { checkFormValues } from "services/common";
import { SapSystem } from "routes/analysis/types/analysis";
import { PostSystem, PutSystem } from "routes/administration/queries/system";

const initSystem: SapSystem = {
  systemId: "new",
  sapNickname: "",
  sapSystemName: "",
  sapClient: ""
}

interface SystemFormProps {
  systemData?: SapSystem;
  cancel: () => void;
}

const SystemForm: FC<SystemFormProps> = ({ systemData, cancel }) => {
  const { clientId } = useParams();

  const [system, setSystem] = useState<SapSystem>(systemData || initSystem);
  const [errors, setErrors] = useState<(keyof SapSystem)[] | undefined>();
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { mutate: addSystem, isPending: addingSystem } = PostSystem(clientId);

  const { mutate: editSystem, isPending: editingSystem } = PutSystem(clientId);

  const handleSubmit = async (system?: SapSystem) => {
    if (!system) return;
    if (!validateForm(system)) return;
    if (system.systemId === "new") {
      system.clientId = clientId || "";
      system.systemId = crypto.randomUUID();
      toastMessage("Adding system");
      addSystem(system, {
        onSuccess: () => {
          toastMessage("System added successfully");
          queryClient.invalidateQueries({ queryKey: ["systems", clientId] });
          cancel();
          return true;
        },
        onError: () => {
          setError("Unable to add a system");
        }
      });
    } else {
      toastMessage("Updating system");
      editSystem(system, {
        onSuccess: () => {
          toastMessage("System updated successfully");
          queryClient.invalidateQueries({ queryKey: ["systems", clientId] });
          cancel();
          return true;
        },
        onError: () => {
          setError("Unable to update system");
        }
      });
    }
  }

  const validateForm = (system: SapSystem) => {
    setErrors(undefined);
    setError(null);
    let errorList: (keyof SapSystem)[] = [];
    let check = checkFormValues(system, ["sapClient", "sapNickname", "sapSystemName"]);
    if (Array.isArray(check))
      errorList = check;
    if (errorList.length > 0) {
      setErrors(errorList);
      return false;
    }
    return true;
  }

  return (
    <div className="ap-input-form">
      <div className="flex flex-col gap-4">
        <div>
          <Input
            type="text"
            value={system?.sapNickname || ""}
            title="Nickname"
            onChange={(value: string) => setSystem({ ...system, sapNickname: value })}
            required
            error={errors?.includes("sapNickname")}
            errorNode={<ValidationError error="Nickname is required" />}
          />
        </div>
        <div>
          <Input
            type="text"
            value={system?.sapSystemName || ""}
            title="System name"
            onChange={(value: string) => setSystem({ ...system, sapSystemName: value })}
            required
            error={errors?.includes("sapSystemName")}
            errorNode={<ValidationError error="System name is required" />}
          />
        </div>
        <div>
          <Input
            type="text"
            value={system?.sapClient || ""}
            title="Client"
            onChange={(value: string) => setSystem({ ...system, sapClient: value })}
            required
            error={errors?.includes("sapClient")}
            errorNode={<ValidationError error="Client is required" />}
          />
        </div>
      </div>
      <FormError error={error} />
      <div className="ap-footer flex gap-4">
        <div className="grow">
          <Button onClick={cancel} kind="secondary">Cancel</Button>
        </div>
        <div>
          <Button
            onClick={() => handleSubmit(system)}
            kind="primary"
            loading={editingSystem || addingSystem}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SystemForm;
